import type { ClockPrepUsageClock, ClockPrepUsageClockItem } from '../api/showClockPrepUsage'
import { resolveClockItemTypes } from './showClock'

const formatDayLabel = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) {
    return date
  }
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(parsed)
}

const extractTimeHHMM = (value: string | null): string | null => {
  if (!value) {
    return null
  }
  const cleaned = value.replace('T', ' ').trim()
  if (cleaned.length >= 16) {
    return cleaned.slice(11, 16)
  }
  const match = cleaned.match(/(\d{2}):(\d{2})(?::\d{2})?$/)
  if (match) {
    return `${match[1]}:${match[2]}`
  }
  return null
}

const buildItemTypeMap = (): Map<string, string> => {
  const map = new Map<string, string>()
  resolveClockItemTypes().forEach((entry) => map.set(entry.key, entry.label))
  return map
}

const itemTypeMap = buildItemTypeMap()

const getItemTypeLabel = (key: string): string => itemTypeMap.get(key) ?? key

export const formatShowClockUsageLabel = (clock: Pick<ClockPrepUsageClock, 'date' | 'window_start' | 'title'>): string => {
  const date = typeof clock.date === 'string' && clock.date.trim().length > 0 ? clock.date.trim() : null
  const title = typeof clock.title === 'string' ? clock.title.trim() : ''
  const start = extractTimeHHMM(typeof clock.window_start === 'string' ? clock.window_start : null)

  const parts: string[] = [date ? formatDayLabel(date) : 'Unknown date']
  if (start) {
    parts.push(start)
  }
  if (title) {
    parts.push(title)
  }
  return parts.join(' • ')
}

export const formatShowClockUsageItemLabel = (item: ClockPrepUsageClockItem): string => {
  const timeLabel = extractTimeHHMM(typeof item.scheduled_start === 'string' ? item.scheduled_start : null)
  const typeLabel = getItemTypeLabel(item.item_type ?? '')
  const title = typeof item.title === 'string' ? item.title.trim() : ''
  const base = title ? `${typeLabel} — ${title}` : typeLabel
  return timeLabel ? `[${timeLabel}] ${base}` : base
}

export const capShowClockUsage = (
  clocks: readonly ClockPrepUsageClock[],
  limit: number = 3,
): { clocks: ClockPrepUsageClock[]; extraCount: number } => {
  const safe = Array.isArray(clocks) ? clocks : []
  const sliced = safe.slice(0, Math.max(0, limit))
  const extraCount = safe.length > sliced.length ? safe.length - sliced.length : 0
  return { clocks: sliced, extraCount }
}

export const capShowClockUsageItems = (
  items: readonly ClockPrepUsageClockItem[],
  limit: number = 3,
): { items: ClockPrepUsageClockItem[]; extraCount: number } => {
  const safe = Array.isArray(items) ? items : []
  const sliced = safe.slice(0, Math.max(0, limit))
  const extraCount = safe.length > sliced.length ? safe.length - sliced.length : 0
  return { items: sliced, extraCount }
}
